import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { ForteXApi, MapNodeSettings, MapRootName, MasterDataModel } from "@fortex/api";
import { Field, Input, Segmented, Switch, TextArea, format } from "@fortex/map";
import type { FieldErrors } from "../shared/CrudPage";
import type { AppLabels } from "../../i18n";
import { LayerDataFields } from "./LayerDataFields";
import { KindSettings } from "./SettingsFields";
import type { SettingsPatch } from "./SettingsFields";
import { StyleFields } from "./StyleFields";
import { allowedKinds, kindOf, mapRootOf, rootPrefix, settingsJson, settingsOf, switchKind } from "./treeModel";
import type { AdminKind, IdNameProblem } from "./treeModel";

export const validateDelayMs = 400;

export type ParentOption = { id: number; label: string; idName: string | null };

export type MasterDataFormProps = {
  api: ForteXApi;
  draft: MasterDataModel;
  setDraft: (draft: MasterDataModel) => void;
  errors: FieldErrors;
  labels: AppLabels;
  parents: ParentOption[];
  hasChildren: boolean;
  readOnly: boolean;
  layerVersion?: number;
};

const kindLabelKeys: Record<AdminKind, keyof AppLabels> = { Group: "kindGroup", Tile: "kindTile", Special: "kindSpecial", Folder: "kindFolder", GeoObject: "kindGeoObject" };

export function idNameMessage(problem: IdNameProblem | null, root: MapRootName | null, labels: AppLabels): string | null {
  switch (problem) {
    case "required":
      return labels.idNameRequired;
    case "prefix":
      return format(labels.idNamePrefix, { prefix: root === null ? "" : rootPrefix[root] });
    case "chars":
      return labels.idNameChars;
    case "taken":
      return labels.idNameTaken;
    default:
      return null;
  }
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="fxapp-form-section">
      <h3 className="fxapp-settings-title">{title}</h3>
      {children}
    </section>
  );
}

function MapNodeSection({ api, draft, setDraft, labels, root, hasChildren, readOnly, layerVersion = 0 }: Omit<MasterDataFormProps, "parents" | "errors"> & { root: MapRootName }) {
  const kind = kindOf(draft.value, hasChildren).kind;
  const settings = useMemo(() => settingsOf(draft), [draft]);
  const [json, setJson] = useState(() => (draft.value2 && draft.value2.trim().length > 0 ? draft.value2 : settingsJson(settings)));
  const [jsonValid, setJsonValid] = useState(true);
  const [validation, setValidation] = useState<{ valid: boolean; problems: string[] } | null>(null);
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    if (!jsonValid) return;

    const controller = new AbortController();
    const timer = setTimeout(() => {
      setValidating(true);
      api
        .validateSettings(kind, root, json, controller.signal)
        .then(result => {
          if (controller.signal.aborted) return;

          setValidation(result);
          setValidating(false);
        })
        .catch(() => {
          if (!controller.signal.aborted) setValidating(false);
        });
    }, validateDelayMs);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [api, kind, root, json, jsonValid]);

  const applySettings = (next: MapNodeSettings, nextKind: AdminKind = kind) => {
    const text = settingsJson(next);

    setJson(text);
    setJsonValid(true);
    setDraft({ ...draft, value: nextKind, value2: text });
  };

  const patchSettings = (patch: SettingsPatch) => applySettings({ ...settings, ...patch });
  const changeKind = (next: AdminKind) => applySettings(switchKind(settings, next, root), next);

  const onJsonChange = (text: string) => {
    setJson(text);

    try {
      const parsed = JSON.parse(text) as unknown;

      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        setJsonValid(true);
        setDraft({ ...draft, value2: text });
      } else setJsonValid(false);
    } catch {
      setJsonValid(false);
    }
  };

  const kindOptions = allowedKinds[root].map(value => ({ value, label: labels[kindLabelKeys[value]] as string, disabled: readOnly || (hasChildren && value !== "Group") }));

  return (
    <Section title={labels.sectionMapSettings}>
      <Field label={labels.kind}>
        <Segmented<AdminKind> label={labels.kind} value={kind} options={kindOptions} onChange={changeKind} dataFxmap="node-kind" />
      </Field>
      <div className="fxapp-settings" data-fxmap="kind-settings" data-kind={kind}>
        <KindSettings kind={kind} root={root} settings={settings} labels={labels} onChange={patchSettings} />
      </div>
      {kind === "GeoObject" ? <StyleFields attr={draft.attr} icon={draft.masterDataIcon} labels={labels} onAttrChange={attr => setDraft({ ...draft, attr })} onIconChange={masterDataIcon => setDraft({ ...draft, masterDataIcon })} /> : null}
      <Field label={labels.settingsJson} error={jsonValid ? null : labels.settingsInvalidJson} hint={validating ? labels.validating : validation !== null && validation.valid ? labels.noProblems : null}>
        <TextArea mono rows={8} value={json} readOnly={readOnly} onChange={event => onJsonChange(event.target.value)} data-fxmap="settings-json" />
      </Field>
      {validation !== null && !validation.valid && validation.problems.length > 0 ? (
        <ul className="fxapp-problem-list" data-fxmap="validation-problems">
          {validation.problems.map(problem => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      ) : null}
      {(kind === "Folder" || kind === "GeoObject") && draft.id > 0 ? <LayerDataFields api={api} id={draft.id} labels={labels} readOnly={readOnly} version={layerVersion} /> : null}
    </Section>
  );
}

export function MasterDataForm({ api, draft, setDraft, errors, labels, parents, hasChildren, readOnly, layerVersion = 0 }: MasterDataFormProps) {
  const root = mapRootOf(draft.idName);
  const nameHu = draft.name?.lang?.[0] ?? "";
  const nameEn = draft.name?.lang?.[1] ?? "";

  const setName = (index: 0 | 1, value: string) => {
    const lang = [...(draft.name?.lang ?? ["", ""])];

    while (lang.length < 2) lang.push("");

    lang[index] = value;
    setDraft({ ...draft, name: { ...(draft.name ?? {}), lang } });
  };

  const parentValue = draft.parentMasterData_Id === null ? "" : String(draft.parentMasterData_Id);

  return (
    <form className="fxapp-form" onSubmit={event => event.preventDefault()} noValidate data-fxmap="masterdata-form">
      <Section title={labels.sectionIdentity}>
        <div className="fxapp-form-row">
          <Field label={labels.colIdName} required error={errors.idName ?? null}>
            <Input mono value={draft.idName} readOnly={readOnly} onChange={event => setDraft({ ...draft, idName: event.target.value.toUpperCase() })} data-fxmap="node-idname" />
          </Field>
          <Field label={labels.colDataId} required error={errors.dataId ?? null}>
            <Input type="number" value={String(draft.dataId)} readOnly={readOnly} onChange={event => setDraft({ ...draft, dataId: Number(event.target.value) || 0 })} />
          </Field>
        </div>
        <Field label={labels.colParent} error={errors.parent ?? null}>
          <select
            className="fxmap-select fxapp-select"
            value={parentValue}
            disabled={readOnly}
            onChange={event => {
              const id = event.target.value === "" ? null : Number(event.target.value);
              const parent = parents.find(a => a.id === id) ?? null;

              setDraft({ ...draft, parentMasterData_Id: id, parentIdName: parent?.idName ?? null });
            }}
            data-fxmap="node-parent"
          >
            <option value="">{labels.noParent}</option>
            {parents.map(parent => (
              <option key={parent.id} value={parent.id}>
                {parent.label}
              </option>
            ))}
          </select>
        </Field>
      </Section>
      <Section title={labels.sectionValues}>
        <div className="fxapp-form-row">
          <Field label={labels.colValue} required={root === null} error={errors.value ?? null}>
            <Input value={draft.value ?? ""} readOnly={readOnly || root !== null} onChange={event => setDraft({ ...draft, value: event.target.value })} data-fxmap="node-value" />
          </Field>
          {root === null ? (
            <Field label={labels.colValue2}>
              <Input value={draft.value2 ?? ""} readOnly={readOnly} onChange={event => setDraft({ ...draft, value2: event.target.value })} />
            </Field>
          ) : null}
        </div>
      </Section>
      <Section title={labels.sectionNames}>
        <div className="fxapp-form-row">
          <Field label={labels.nameHu} required error={errors.nameHu ?? null}>
            <Input value={nameHu} readOnly={readOnly} onChange={event => setName(0, event.target.value)} data-fxmap="node-name-hu" />
          </Field>
          <Field label={labels.nameEn}>
            <Input value={nameEn} readOnly={readOnly} onChange={event => setName(1, event.target.value)} data-fxmap="node-name-en" />
          </Field>
        </div>
      </Section>
      <Section title={labels.sectionAppearance}>
        <div className="fxapp-form-row">
          <Field label={labels.colOrder}>
            <Input type="number" value={String(draft.order_)} readOnly={readOnly} onChange={event => setDraft({ ...draft, order_: Number(event.target.value) || 0 })} />
          </Field>
          <Field label={labels.colVisible} inline>
            <Switch checked={draft.visible} disabled={readOnly} onChange={visible => setDraft({ ...draft, visible })} label={labels.colVisible} />
          </Field>
        </div>
      </Section>
      {root !== null ? <MapNodeSection key={`${draft.id}-${root}`} api={api} draft={draft} setDraft={setDraft} labels={labels} root={root} hasChildren={hasChildren} readOnly={readOnly} layerVersion={layerVersion} /> : null}
    </form>
  );
}
